// src/components/GuidaNexiora.js
// La guida consultabile — diversa dall'onboarding iniziale (OnboardingModal), che si vede una
// volta sola al primo accesso e serve a orientarsi. Questa invece risponde alla domanda che
// viene DOPO, quando si sta già lavorando: "so cosa voglio fare, ma dove sta?".
//
// Tre scelte precise dietro questa schermata:
// 1. Niente ricerca di commesse o documenti — quella è la Ricerca Globale (Ctrl+K). In cima
//    c'è invece «Come posso aiutarti?» (ottobre 2026): risponde alle domande «come si fa»
//    cercando nei testi del Centro assistenza (components/AssistenteGuida.js).
// 2. Ogni riga spiega cosa fa quella sezione in concreto, non con una formula generica —
//    chi legge deve capire SE quella cosa fa al caso suo, non solo che esiste.
// 3. Le sezioni a cui l'utente non ha accesso non spariscono: restano visibili, marcate come
//    "non attiva per te", con la spiegazione di cosa fanno. È lì che una guida diventa
//    davvero utile — fa scoprire quello che c'è e nessuno sa di avere, invece di nascondere
//    porte chiuse e lasciare che una funzionalità utile resti ignorata per sempre.
import React, { useState } from "react";
import { X, ExternalLink, Lock, Search, ListChecks } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useCanSee } from "../hooks/useCanSee";
import { useOrg } from "../contexts/OrgContext";
import { AREE, SEMPRE_DISPONIBILI } from "../utils/guidaContenuti";
import { schedaGuidaEsatta, urlScheda, URL_CENTRO_ASSISTENZA } from "../utils/guidaOnline";
import AssistenteGuida from "./AssistenteGuida";

// Aree tematiche — l'ordine è quello in cui una persona tende a incontrarle lavorando,
// non l'ordine alfabetico né quello della sidebar.
// «Come si fa ↗»: la scheda passo passo sul sito, sempre in una nuova scheda del browser
function LinkComeSiFa({ id }) {
  if (!id) return null;
  return (
    <a href={urlScheda(id)} target="_blank" rel="noopener noreferrer" title="Guida passo passo sul sito (si apre in una nuova scheda)"
      style={{flexShrink:0,display:"flex",alignItems:"center",gap:4,fontSize:11,fontWeight:700,color:"#6b7280",background:"#fff",border:"1px solid #e5e7eb",borderRadius:7,padding:"5px 9px",textDecoration:"none",fontFamily:"'Inter',sans-serif",whiteSpace:"nowrap"}}>
      Come si fa ↗
    </a>
  );
}

/**
 * Quali sezioni può aprire questa persona (stesse condizioni della barra laterale: modulo
 * acceso per l'azienda, poi i permessi). Usato dalla Guida e da Nexi (components/Nexi.js):
 * «Vai alla sezione» compare solo per le pagine che si possono davvero aprire.
 */
export function useSezioniApribili() {
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const canSee = useCanSee();
  const { orgVisitata } = useOrg();
  const accessibile = (v) => {
    if (v.modulo && !orgVisitata?.[v.modulo]) return false;
    if (isOrgAdmin || isPlatformOwner) return true;
    if (v.soloAdmin) return false;
    if (v.libera) return true;
    return (v.perm || [v.id]).some(p => canSee(p));
  };
  const puoAprire = (pagina) => {
    const v = [...AREE.flatMap(a => a.voci), ...SEMPRE_DISPONIBILI].find(x => (x.pagina || x.id) === pagina);
    return !!v && !v.dentroCommessa && (SEMPRE_DISPONIBILI.includes(v) || accessibile(v));
  };
  return { accessibile, puoAprire };
}

export default function GuidaNexiora({ onClose, onNavigate }) {
  const [mostraNonAttive, setMostraNonAttive] = useState(true);
  const { accessibile, puoAprire } = useSezioniApribili();
  const { orgVisitata } = useOrg();
  const vai = (pagina) => { onNavigate && onNavigate({ page: pagina }); onClose(); };

  return (
    <div style={{position:"fixed",inset:0,zIndex:99000,background:"rgba(15,12,41,.75)",display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:"#fff",borderRadius:18,width:"100%",maxWidth:800,maxHeight:"88vh",display:"flex",flexDirection:"column",boxShadow:"0 30px 80px rgba(0,0,0,.4)",overflow:"hidden"}}>

        <div style={{background:"linear-gradient(135deg,#1e1b4b,#3730a3)",padding:"20px 26px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div>
            <h2 style={{fontSize:18,fontWeight:800,color:"#fff",margin:0}}>📖 Guida a Nexiora</h2>
            <p style={{fontSize:12,color:"rgba(255,255,255,.65)",margin:"4px 0 0"}}>Fai una domanda, oppure scopri cosa fa ogni sezione e come arrivarci</p>
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,.12)",border:"none",borderRadius:8,width:32,height:32,cursor:"pointer",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <X size={17}/>
          </button>
        </div>

        <div style={{padding:"14px 26px",borderBottom:"1px solid #f3f4f6",display:"flex",gap:10,alignItems:"center",flexWrap:"wrap",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"#6b7280",background:"#f9fafb",border:"1px solid #e5e7eb",borderRadius:8,padding:"6px 10px"}}>
            <Search size={13}/> Cerchi qualcosa di preciso? Premi <strong style={{color:"#374151"}}>Ctrl+K</strong> per la ricerca globale
          </div>
          <button onClick={()=>{ onClose(); window.dispatchEvent(new CustomEvent("nexiora:onboarding")); }}
            style={{display:"flex",alignItems:"center",gap:6,fontSize:12,fontWeight:600,color:"#6d28d9",background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"6px 10px",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
            <ListChecks size={13}/> Rivedi i primi passi
          </button>
          <a href={URL_CENTRO_ASSISTENZA} target="_blank" rel="noopener noreferrer" title="Il Centro assistenza sul sito, con ogni funzione spiegata passo passo (si apre in una nuova scheda)"
            style={{display:"flex",alignItems:"center",gap:6,fontSize:12,fontWeight:600,color:"#6d28d9",background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"6px 10px",textDecoration:"none",fontFamily:"'Inter',sans-serif"}}>
            📘 Centro assistenza ↗
          </a>
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:11.5,color:"#6b7280",marginLeft:"auto",cursor:"pointer"}}>
            <input type="checkbox" checked={mostraNonAttive} onChange={e=>setMostraNonAttive(e.target.checked)} style={{accentColor:"#7c3aed"}}/>
            Mostra anche cosa non è attivo per me
          </label>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:"18px 26px 26px"}}>
          <AssistenteGuida puoAprire={puoAprire} onApri={vai}/>
          {AREE.map(area => {
            const vociVisibili = area.voci.filter(v => accessibile(v) || mostraNonAttive);
            if (vociVisibili.length === 0) return null;
            return (
              <div key={area.nome} style={{marginBottom:22}}>
                <div style={{fontSize:13,fontWeight:800,color:"#1e1b4b",marginBottom:8,display:"flex",alignItems:"center",gap:7}}>
                  <span style={{fontSize:16}}>{area.emoji}</span> {area.nome}
                </div>
                {vociVisibili.map(v => {
                  const ok = accessibile(v);
                  return (
                    <div key={v.id} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"9px 12px",borderRadius:9,marginBottom:5,
                      background: ok?"#fff":"#fafafa", border:`1px solid ${ok?"#f3f4f6":"#f3f4f6"}`, opacity: ok?1:.72}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,color: ok?"#111827":"#9ca3af",display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                          {v.label}
                          {!ok && <span style={{display:"flex",alignItems:"center",gap:3,fontSize:9.5,fontWeight:700,color:"#92400e",background:"#fffbeb",border:"1px solid #fde68a",padding:"1px 6px",borderRadius:5}}>
                            <Lock size={9}/> {v.modulo && !orgVisitata?.[v.modulo] ? "MODULO NON ATTIVO" : "NON ATTIVA PER TE"}
                          </span>}
                        </div>
                        <div style={{fontSize:11.5,color:"#6b7280",lineHeight:1.55,marginTop:2}}>{v.cosa}</div>
                      </div>
                      <LinkComeSiFa id={v.dentroCommessa ? schedaGuidaEsatta("project", v.id) : schedaGuidaEsatta(v.pagina || v.id)}/>
                      {ok && v.dentroCommessa && (
                        <span style={{flexShrink:0,fontSize:10,color:"#9ca3af",fontStyle:"italic",whiteSpace:"nowrap",paddingTop:3}}>si apre dalla commessa</span>
                      )}
                      {ok && !v.dentroCommessa && (
                        <button onClick={()=>vai(v.pagina || v.id)} title={`Apri ${v.label}`}
                          style={{flexShrink:0,display:"flex",alignItems:"center",gap:4,fontSize:11,fontWeight:700,color:"#6d28d9",background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:7,padding:"5px 10px",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                          Apri <ExternalLink size={11}/>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}

          <div style={{marginBottom:22}}>
            <div style={{fontSize:13,fontWeight:800,color:"#1e1b4b",marginBottom:8,display:"flex",alignItems:"center",gap:7}}>
              <span style={{fontSize:16}}>🧰</span> Sempre disponibili, per tutti
            </div>
            {SEMPRE_DISPONIBILI.map(v => (
              <div key={v.label} style={{display:"flex",gap:10,alignItems:"flex-start",padding:"9px 12px",borderRadius:9,marginBottom:5,background:"#fff",border:"1px solid #f3f4f6"}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:700,color:"#111827"}}>{v.label}</div>
                  <div style={{fontSize:11.5,color:"#6b7280",lineHeight:1.55,marginTop:2}}>{v.cosa}</div>
                </div>
                <LinkComeSiFa id={schedaGuidaEsatta(v.pagina)}/>
                <button onClick={()=>vai(v.pagina)} title={`Apri ${v.label}`}
                  style={{flexShrink:0,display:"flex",alignItems:"center",gap:4,fontSize:11,fontWeight:700,color:"#6d28d9",background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:7,padding:"5px 10px",cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                  Apri <ExternalLink size={11}/>
                </button>
              </div>
            ))}
          </div>

          <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:"12px 16px",fontSize:11.5,color:"#5b21b6",lineHeight:1.6}}>
            💜 Le sezioni marcate «non attiva per te» esistono in Nexiora ma non sono abilitate per il tuo utente — se pensi che ti servano, chiedi a chi amministra l'organizzazione: si abilitano in un attimo da Gestione → Abilitazioni, per ruolo o per singola persona.
          </div>
        </div>
      </div>
    </div>
  );
}
